import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Clock, LogOut } from 'lucide-react';
import { SessionService } from '@/services/sessionService';
import { useNavigate } from 'react-router-dom';

export const SessionWarning: React.FC = () => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [minutesLeft, setMinutesLeft] = useState(5);
  const [isExtending, setIsExtending] = useState(false);

  useEffect(() => {
    // Subscribe to session warning
    const unsubscribeWarning = SessionService.onSessionWarning((minutes) => {
      setMinutesLeft(minutes);
      setShowWarning(true);
    });

    // Subscribe to session expiry
    const unsubscribeExpiry = SessionService.onSessionExpire(() => {
      setShowWarning(false);
      navigate('/login', { 
        state: { 
          message: 'Your session has expired. Please log in again.' 
        } 
      });
    });

    // Cleanup
    return () => {
      unsubscribeWarning();
      unsubscribeExpiry();
    };
  }, [navigate]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      SessionService.extendSession();
      setShowWarning(false);
      setIsExtending(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
      setIsExtending(false);
    }
  };

  const handleLogout = () => {
    SessionService.endSession();
    navigate('/login');
  };

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Session Expiring Soon
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center py-4" asChild>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">
                {minutesLeft} {minutesLeft === 1 ? 'minute' : 'minutes'} remaining
              </div>
              <p className="text-sm text-gray-600">
                Your session will expire due to inactivity.
                Would you like to continue working?
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleLogout}
            disabled={isExtending}
            className="w-full sm:w-auto"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
          <AlertDialogAction
            onClick={handleExtendSession}
            disabled={isExtending}
            className="bg-[#183C2E] hover:bg-[#153628] w-full sm:w-auto"
          >
            {isExtending ? 'Extending...' : 'Continue Working'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};